/**
 * @description Utilities class for working with date and time
 */
import moment from "moment";
import DateTimeFormatter from "@utils/datetime.formatter.ts";
// Time zone (GMT+8) is offset by -480
const STANDARD_TIMEZONE_OFFSET = -480;

const DateTimeUtils = {
  formats: {
    localDateTime: "YYYYMMDDHHmmss",
    localDate: "YYYYMMDD",
    year: "YYYY",
    dateTime: "YYYY-MM-DDTHH:mm:ss",
    dateTimeWithoutSec: "YYYY-MM-DDTHH:mm",
    dateTimeAmPm: "DD MMM YYYY hh:mm A",
    dateOnly: "DD MMM YYYY",
    dateNumeric: "DD/MM/YYYY",
    dateYearFirst: "YYYY-MM-DD",
    timeAmPm: "hh:mm A",
  },
  momentKeys: {
    years: "years",
    months: "months",
    weeks: "weeks",
    days: "days",
    hours: "hours",
    minutes: "minutes",
    seconds: "seconds",
  },
  momentInclusivity: {
    excludeAll: "()",
    includeFrom: "[)",
    includeTo: "(]",
    includeAll: "[]",
  },
  timeZoneSG: "Asia/Singapore",
  parseUnixTimestamp(unixTime) {
    return moment(unixTime).toDate();
  },
  parseLocalDateTime(dateTime) {
    return moment(dateTime, this.formats.localDateTime).toDate();
  },
  parseDateTime(dateTime) {
    return moment(dateTime, this.formats.dateTime).toDate();
  },
  isNowAfterDate: function (dateTime) {
    let nowTime = new Date().toLocaleString("en-US", { timeZone: this.timeZoneSG });
    return moment(nowTime).isAfter(dateTime);
  },
  isNowBeforeDate: function (dateTime) {
    let nowTime = new Date().toLocaleString("en-US", { timeZone: this.timeZoneSG });
    return moment(nowTime).isBefore(dateTime);
  },
  isBeforeDate: function (dateTime1, dateTime2) {
    return moment(dateTime1).isBefore(dateTime2);
  },
  isSameOrBeforeDate: function (dateTime1, dateTime2) {
    return moment(dateTime1).isSame(dateTime2, "day") || moment(dateTime1).isBefore(dateTime2);
  },
  isAfterDate: function (dateTime1, dateTime2) {
    return moment(dateTime1).isAfter(dateTime2);
  },
  isSameOrAfterDate: function (dateTime1, dateTime2) {
    return moment(dateTime1).isSame(dateTime2, "day") || moment(dateTime1).isAfter(dateTime2);
  },
  isSameOrAfterDateTimeSecond: function (dateTime1, dateTime2) {
    return (
      moment(dateTime1).isSame(dateTime2, "second") ||
      moment(dateTime1).isAfter(dateTime2, "second")
    );
  },
  isSameDate: function (dateTime1, dateTime2) {
    return moment(dateTime1).isSame(dateTime2, "day");
  },
  isSameDatetimeType: function (dateTime1, dateTime2, datetimeType) {
    return moment(dateTime1).isSame(dateTime2, datetimeType);
  },
  isValid: function (dateTime, format) {
    return moment(dateTime, format, true).isValid();
  },
  convertLocalDateToMoment(date) {
    return moment(date, this.formats.localDate);
  },
  convertLocalDateTimeToMoment(dateTime) {
    return moment(dateTime, this.formats.localDateTime, true);
  },
  convertDateTimeToMoment(dateTime) {
    return moment(dateTime, this.formats.dateTime, true);
  },
  convertDateTimeStringToUnixTimestamp(dateTime) {
    // return milliseconds
    return moment(dateTime).valueOf();
  },
  getServerTime() {
    // Return current Singapore time (+8 GMT) which SLS is using
    return new Date().toLocaleString("en-US", { timeZone: this.timeZoneSG });
  },
  getServerTimeInUnixTimestamp() {
    return this.convertDateTimeStringToUnixTimestamp(this.getServerTime());
  },
  getHours: function (seconds) {
    if (!seconds) return 0;
    const duration = moment.duration(seconds, "seconds");
    return duration.hours();
  },
  getHoursOnly: function (seconds) {
    if (!seconds) return 0;
    const duration = moment.duration(seconds, "seconds");
    return parseInt(duration.asHours());
  },
  getMinutes: function (seconds) {
    if (!seconds) return 0;
    const duration = moment.duration(seconds, "seconds");
    return duration.minutes();
  },
  getMinutesOnly: function (seconds) {
    if (!seconds) return 0;
    const duration = moment.duration(seconds, "seconds");
    return parseInt(duration.asMinutes());
  },
  getSeconds: function (seconds) {
    if (!seconds) return 0;
    const duration = moment.duration(seconds, "seconds");
    return duration.seconds();
  },
  /**
   * convert string or object time to minutes
   * @param {String|Object} timeObject object: { momentKeys: value }; String: "[days.]hrs:mins[:secs[.milsecs]]", hours & minutes compulsory
   * @returns {Number}
   */
  getMinutesFromTimeObject: function (timeObject) {
    return moment.duration(timeObject).asMinutes();
  },
  /**
   * @param {String} time - Time in 24 hrs format.
   * @example
   * DateTimeUtils.timeFormatValid("13:30");
   * eg: Valid Format - "23:59"
   * @return {Boolean} - True: Valid Format / False: Invalid Format.
   */
  timeFormatValid: function (time) {
    let regex = /^([01]\d|2[0-3]):([0-5]\d)$/;
    return regex.test(time);
  },
  /**
   * Function to revert back to previous valid date
   * @param {String} newDate - Date to be validated.
   * @param {String} originalDate - Previous valid date to be reverted.
   * @example
   * DateTimeUtils.parseDate("03aa Novee 2020", "03 Nov 2020");
   * @return {Date} - String of date in valid format.
   */
  parseDate(newDate, originalDate) {
    if (
      this.isValid(newDate, this.formats.dateOnly) ||
      this.isValid(newDate, this.formats.dateYearFirst)
    ) {
      return new Date(newDate);
    }
    return new Date(originalDate);
  },
  /**
   * Function to retrieve 12 months range
   * eg. [Jan, Feb, March...]
   */
  getMonthsShortRange() {
    return moment.monthsShort();
  },
  isValidTimezone() {
    let userTimezoneOffset = new Date().getTimezoneOffset();
    return userTimezoneOffset === STANDARD_TIMEZONE_OFFSET;
  },
  /**
   * Function to convert milliseconds to ISO 8601 duration format
   * @param milliSeconds eg: 300000
   * @returns {string} eg: "PT5M"
   */
  getDurationInISOFormat(milliSeconds) {
    let duration = moment.duration(milliSeconds);
    return duration.toISOString();
  },
  /**
   * Accept local date time format and add 1 day
   * @param {String} input local datetime format
   * @returns {String} datetime format
   */
  nextDay(input) {
    return moment(input, this.formats.localDateTime)
      .add(1, this.momentKeys.days)
      .format(this.formats.dateTime);
  },
  /**
   * Accept any format and set time to start of day (00:00:00)
   * @param {String} input
   * @param {String} inputFormat
   * @returns {String} datetime format
   */
  startOfDay(input, inputFormat) {
    return moment(input, inputFormat).startOf("day").format(this.formats.dateTime);
  },
  /**
   * Accept any format and set time to end of day (23:59:59)
   * @param {String} input
   * @param {String} inputFormat
   * @returns {String} datetime format
   */
  endOfDay(input, inputFormat) {
    return moment(input, inputFormat).endOf("day").format(this.formats.dateTime);
  },
  /**
   * Accept any format and set datetime to start of week (YYYY-MM-01T00:00:00)
   * @param {String} input
   * @param {String} inputFormat
   * @returns {String} datetime format
   */
  startOfWeek(input, inputFormat) {
    return moment(input, inputFormat).startOf("week").format(this.formats.dateTime);
  },
  /**
   * Accept any format and set datetime to end of week (YYYY-MM-07T23:59:59)
   * @param {String} input
   * @param {String} inputFormat
   * @returns {String} datetime format
   */
  endOfWeek(input, inputFormat) {
    return moment(input, inputFormat).endOf("week").format(this.formats.dateTime);
  },
  localDateBuilder(dateTimeObject) {
    return moment(dateTimeObject).format(this.formats.localDate);
  },
  /**
   * Accept any format and add number of weeks
   * @param {String} input
   * @param {String} inputFormat
   * @param {Number|String} noOfWeeks
   * @returns {String} datetime format
   */
  addWeek(input, inputFormat, noOfWeeks) {
    return moment(input, inputFormat)
      .add(noOfWeeks, this.momentKeys.weeks)
      .format(this.formats.dateTime);
  },
  /**
   * Accept any format and add number of days
   * @param {String} input
   * @param {String} inputFormat
   * @param {Number|String} noOfDays
   * @returns {String} datetime format
   */
  addDay(input, inputFormat, noOfDays) {
    return moment(input, inputFormat)
      .add(noOfDays, this.momentKeys.days)
      .format(this.formats.dateTime);
  },
  /**
   * Accept any format and add number of hours
   * @param {String} input
   * @param {String} inputFormat
   * @param {Number|String} noOfHours
   * @returns {String} datetime format
   */
  addHour(input, inputFormat, noOfHours) {
    return moment(input, inputFormat)
      .add(noOfHours, this.momentKeys.hours)
      .format(this.formats.dateTime);
  },
  /**
   * Accept any format and add number of minutes
   * @param {String} input
   * @param {String} inputFormat
   * @param {Number|String} noOfMinutes
   * @returns {String} datetime format
   */
  addMinute(input, inputFormat, noOfMinutes) {
    return moment(input, inputFormat)
      .add(noOfMinutes, this.momentKeys.minutes)
      .format(this.formats.dateTime);
  },
  /**
   * Accept any format and add number of seconds
   * @param {String} input
   * @param {String} inputFormat
   * @param {Number|String} noOfSeconds
   * @returns {String} datetime format
   */
  addSecond(input, inputFormat, noOfSeconds) {
    return moment(input, inputFormat)
      .add(noOfSeconds, this.momentKeys.seconds)
      .format(this.formats.dateTime);
  },
  /**
   * Accept any format and add datetime
   * @param {String} input
   * @param {String} inputFormat
   * @param {String} datetimeType hours|minutes
   * @param {Number|String} datetimeValue
   * @returns {String} datetime format
   */
  addDateTime(input, inputFormat, datetimeType, datetimeValue) {
    switch (datetimeType) {
      case this.momentKeys.weeks:
        return this.addWeek(input, inputFormat, datetimeValue);
      case this.momentKeys.days:
        return this.addDay(input, inputFormat, datetimeValue);
      case this.momentKeys.hours:
        return this.addHour(input, inputFormat, datetimeValue);
      case this.momentKeys.minutes:
        return this.addMinute(input, inputFormat, datetimeValue);
      case this.momentKeys.seconds:
        return this.addSecond(input, inputFormat, datetimeValue);
      default:
        return moment(input, inputFormat).format(this.formats.dateTime);
    }
  },
  addTimeZoneOffset(input, inputFormat) {
    let timeZoneOffset = new Date(input).getTimezoneOffset() - STANDARD_TIMEZONE_OFFSET;
    return this.addMinute(input, inputFormat, timeZoneOffset);
  },
  isBetweenDatesInDay(dateCompare, dateFrom, dateTo, inclusivity) {
    return moment(dateCompare).isBetween(dateFrom, dateTo, this.momentKeys.days, inclusivity);
  },
  /**
   * dateTime1 - dateTime2, difference in dateTimeType
   * @param {String} dateTime1
   * @param {String} dateTime2
   * @param {String} inputFormat refer this.formats
   * @param {String} dateTimeType refer this.momentKeys
   * @param {Boolean} returnDecimal default truncate the result to zero decimal places
   * @returns {Number}
   */
  getDateTimeDifferenceByType(
    dateTime1,
    dateTime2,
    inputFormat,
    dateTimeType,
    returnDecimal = false,
  ) {
    const newDateTime1 = moment(dateTime1, inputFormat);
    const newDateTime2 = moment(dateTime2, inputFormat);
    return newDateTime1.diff(newDateTime2, dateTimeType, returnDecimal);
  },
  getTimeDifference(dateTime1, dateTime2) {
    const date1 = new Date(dateTime1);
    const date2 = dateTime2
      ? new Date(dateTime2)
      : this.parseUnixTimestamp(this.getServerTimeInUnixTimestamp());
    return Math.abs(date1.getTime() - date2.getTime()) / 1000; // in seconds
  },
  getGreatestDurationBetween(
    dateTime1,
    dateTime2,
    timeUnits = ["y", "M", "d", "h", "m"],
    displays = ["y", "mo", "d", "h", " min"],
  ) {
    if (timeUnits.length !== displays.length) {
      // invalid inputs
      return "";
    }
    const moment1 = moment(dateTime1);
    const moment2 = moment(dateTime2);
    const duration = moment1.diff(moment2);
    const momentDuration = moment.duration(duration);
    const lastUnit = timeUnits.pop();
    const lastDisplay = displays.pop();
    const units = [...timeUnits];
    for (const index in units) {
      const num = momentDuration.as(units[index]);
      if (num > 1) {
        return `${Math.floor(num)} ${displays[index]}`;
      }
    }
    const num = momentDuration.as(lastUnit);
    return `${Math.max(1, Math.floor(num))} ${lastDisplay}`;
  },
  getDurationBetween(dateTime1, dateTime2, format, unit) {
    const moment1 = moment(dateTime1, format);
    const moment2 = moment(dateTime2, format);
    return moment1.diff(moment2, unit, true);
  },
  /**
   * convert ISO8601 Duration eg. 'PT1H30M' to number of minutes
   * @param {String} duration
   * @returns {Number}
   */
  convertISO8601DurationToMinutes(duration) {
    const regex = /P(?:(\d+)Y)?(?:(\d+)M)?(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?)?/; // Matches PnYnMnDTnHnM
    const match = duration.match(regex);

    if (!match) {
      console.error("Invalid ISO 8601 duration format");
      return null;
    }

    const years = parseInt(match[1] || 0, 10);
    const months = parseInt(match[2] || 0, 10);
    const days = parseInt(match[3] || 0, 10);
    const hours = parseInt(match[4] || 0, 10);
    const minutes = parseInt(match[5] || 0, 10);

    // Assuming 1 year = 365 days for simplicity (return number of minutes)
    return years * 365 * 24 * 60 + months * 30 * 24 * 60 + days * 24 * 60 + hours * 60 + minutes;
  },
  /**
   * Minutes to formatted string eg. '1 h 30 min'
   * @param {Number} minutes
   * @returns {String}
   */
  minutesToFormattedString(minutes) {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = Math.floor(minutes % 60);

    let formattedString = "";

    formattedString += `${hours} hr`;

    if (formattedString.length > 0) {
      formattedString += " ";
    }

    if (remainingMinutes > 9) {
      formattedString += `${remainingMinutes} min`;
    } else {
      formattedString += `0${remainingMinutes} min`;
    }

    return formattedString;
  },
  isDateLessThanXYearOld(dateToCheck, x) {
    // accounts for leap years
    const currentDate = new Date();
    const xYearFromDate = new Date(
      dateToCheck.getFullYear() + x,
      dateToCheck.getMonth(),
      dateToCheck.getDate(),
    );

    return currentDate <= xYearFromDate;
  },
  /**
   * Converts date time in ISO8601 format (LocalDateTime sent from backend):
   * I.e., [2024, 0, 1, 11, 9, 56] // 2024 Jan 1st, 11H 8M 56S
   * into Date only string like 01 Sep 2024
   * @param dateArray
   * @returns {string}
   */
  convertLocalDateTimeToDateOnly(dateArray) {
    if (!Array.isArray(dateArray) && !(dateArray.length >= 3 || dateArray.length <= 6)) {
      console.log("Invalid date format to parse");
      return "";
    }

    return moment([dateArray[0], dateArray[1] - 1, dateArray[2]]).format(this.formats.dateOnly);
  },
  parseUnixTimestampInStandardTimeZone(timestamp) {
    let dateTime = this.parseUnixTimestamp(timestamp);
    if (!this.isValidTimezone()) {
      dateTime = this.addTimeZoneOffset(dateTime, DateTimeFormatter.formats.dateTime);
    }
    return dateTime;
  },
};

export default DateTimeUtils;
